import {useCallback, useMemo, useRef, useState} from 'react'
import {useOutsideClick} from '@chakra-ui/react'

const useAutocomplete = ({
    getOptionLabel = (option) => `${option.label || option.value || option}`,
    groupedSuggestions,
    id = 'autocomplete',
    initialValue = '',
    inputProps: {onChange: parentOnChange, value: controlledValue, ...inputProps} = {},
    isLoading,
    optionProps,
    onSuggestionSelected,
    onSuggestionsClearRequested,
    onSuggestionsFetchRequested
}) => {
    const [_inputValue, setInputValue] = useState(initialValue)
    const inputValue = controlledValue ?? _inputValue
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef(null)
    const [activeIndex, setActiveIndex] = useState({groupIndex: -1, optionIndex: -1})

    const labelId = `${id}-label`
    const descriptionId = `${id}-description`
    const sectionsListId = `${id}-sections`

    const clearSuggestions = useCallback(() => {
        onSuggestionsClearRequested?.()
        setIsOpen(false)
    }, [onSuggestionsClearRequested])

    useOutsideClick({
        ref: containerRef,
        handler: () => {
            if (isOpen) {
                clearSuggestions()
            }
        }
    })

    const handleInputChange = useCallback(
        (event) => {
            const value = event.target.value
            setInputValue(value)
            setActiveIndex({groupIndex: -1, optionIndex: -1})

            if (value.length > 0) {
                setIsOpen(true)
                onSuggestionsFetchRequested?.({value})
            } else {
                clearSuggestions()
            }

            parentOnChange?.(event, {newValue: value, method: 'input'})
        },
        [clearSuggestions, parentOnChange, onSuggestionsFetchRequested]
    )

    const handleSelect = useCallback(
        (event, option, onClick = null) => {
            onClick?.(event, option)
            const newValue = onSuggestionSelected?.(event, option) || getOptionLabel(option)
            setInputValue((curValue) => newValue ?? curValue)
            clearSuggestions()
            parentOnChange?.(event, {newValue: option.label, method: 'select'})
        },
        [clearSuggestions, getOptionLabel, parentOnChange, onSuggestionSelected]
    )

    const handleKeyDown = useCallback(
        (event) => {
            if (!isOpen) return

            const options = groupedSuggestions.flatMap((group, groupIndex) =>
                group.suggestions.map((suggestion, optionIndex) => ({
                    groupIndex,
                    optionIndex,
                    suggestion
                }))
            )

            if (isLoading || options.length === 0) {
                if (event.key === 'Escape') {
                    clearSuggestions()
                }
                return
            }

            let currentOptionIndex = -1
            if (activeIndex.groupIndex !== -1 && activeIndex.optionIndex !== -1) {
                currentOptionIndex = options.findIndex(
                    (option) =>
                        option.groupIndex === activeIndex.groupIndex &&
                        option.optionIndex === activeIndex.optionIndex
                )
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault()
                const nextIndex = (currentOptionIndex + 1) % options.length
                setActiveIndex({
                    groupIndex: options[nextIndex].groupIndex,
                    optionIndex: options[nextIndex].optionIndex
                })
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                const prevIndex =
                    (Math.max(0, currentOptionIndex) - 1 + options.length) % options.length
                setActiveIndex({
                    groupIndex: options[prevIndex].groupIndex,
                    optionIndex: options[prevIndex].optionIndex
                })
            } else if (event.key === 'Enter') {
                if (currentOptionIndex !== -1) {
                    event.preventDefault()
                    handleSelect(event, options[currentOptionIndex])
                }
            } else if (event.key === 'Escape') {
                event.preventDefault()
                clearSuggestions()
            }
        },
        [activeIndex, clearSuggestions, groupedSuggestions, handleSelect, isLoading, isOpen]
    )

    const getGroupId = useCallback((groupIndex) => `${id}-group-${groupIndex}`, [id])

    const getOptionId = useCallback(
        (groupIndex, optionIndex) => `${getGroupId(groupIndex)}-option-${optionIndex}`,
        [getGroupId]
    )

    const activeDescendant =
        activeIndex.groupIndex !== -1 && activeIndex.optionIndex !== -1 && !isLoading
            ? getOptionId(activeIndex.groupIndex, activeIndex.optionIndex)
            : null

    const shouldShowDropdown =
        isOpen && (isLoading || inputValue.length > 0 || groupedSuggestions?.length > 0)

    const isOptionActive = useCallback(
        (groupIndex, optionIndex) =>
            activeIndex.groupIndex === groupIndex && activeIndex.optionIndex === optionIndex,
        [activeIndex]
    )

    const getAnnouncementProps = useCallback(
        ({children, ...props}) => ({
            'data-testid': 'autocomplete-announcement-region',
            'aria-live': 'assertive',
            children: shouldShowDropdown && !isLoading && children,
            ...props
        }),
        [shouldShowDropdown, isLoading]
    )

    const getDescriptionProps = useCallback(
        (props) => ({
            id: descriptionId,
            ...props
        }),
        [descriptionId]
    )

    const getDropdownProps = useCallback((dropdownProps) => {
        return dropdownProps
    }, [])

    const getInputProps = useCallback(
        (props) => ({
            id,
            value: inputValue,
            placeholder: 'Type to search...',
            ...inputProps,
            ...props,
            onChange: handleInputChange,
            onKeyDown: handleKeyDown,
            'aria-autocomplete': 'list',
            'aria-controls': sectionsListId,
            'aria-expanded': shouldShowDropdown,
            'aria-activedescendant': activeDescendant,
            'aria-haspopup': 'listbox',
            'aria-describedby': descriptionId
        }),
        [
            id,
            inputValue,
            inputProps,
            handleInputChange,
            handleKeyDown,
            shouldShowDropdown,
            activeDescendant,
            sectionsListId,
            descriptionId
        ]
    )

    const getLabelProps = useCallback(
        (props) => ({
            htmlFor: id,
            id: labelId,
            ...props
        }),
        [id, labelId]
    )

    const getOptionProps = useCallback(
        ({id: forcedId, onClick, groupIndex, optionIndex, ...props}) => {
            if (groupIndex == null || optionIndex == null) {
                throw new Error('groupIndex and optionIndex are required for getOptionProps')
            }
            if (forcedId) {
                throw new Error('custom option id is not supported, use getOptionId instead')
            }
            const id = getOptionId(groupIndex, optionIndex)
            const isActive = isOptionActive(groupIndex, optionIndex)
            return {
                ...optionProps,
                ...props,
                id,
                role: 'option',
                'aria-selected': isActive,
                onClick: (event) => {
                    handleSelect(
                        event,
                        {
                            suggestion: groupedSuggestions[groupIndex].suggestions[optionIndex],
                            groupIndex,
                            optionIndex
                        },
                        onClick
                    )
                }
            }
        },
        [getOptionId, groupedSuggestions, handleSelect, isOptionActive, optionProps]
    )

    const getOptionsListProps = useCallback(
        (props) => ({
            id: sectionsListId,
            role: 'listbox',
            'aria-labelledby': labelId,
            ...props
        }),
        [sectionsListId, labelId]
    )

    const getContainerProps = useCallback(({ref, ...containerProps}) => {
        return {
            ref: ref
                ? containerRef
                : (node) => {
                      containerRef.current = node
                      if (typeof ref === 'function') {
                          ref(node)
                      } else if (ref) {
                          ref.current = node
                      }
                  },
            ...containerProps
        }
    }, [])

    return useMemo(
        () => ({
            activeIndex,
            getAnnouncementProps,
            getContainerProps,
            getDescriptionProps,
            getDropdownProps,
            getInputProps,
            getLabelProps,
            getOptionProps,
            getOptionsListProps,
            isOpen,
            isOptionActive,
            setActiveIndex,
            setInputValue,
            setIsOpen,
            shouldShowDropdown
        }),
        [
            activeIndex,
            getAnnouncementProps,
            getContainerProps,
            getDescriptionProps,
            getDropdownProps,
            getInputProps,
            getLabelProps,
            getOptionProps,
            getOptionsListProps,
            isOpen,
            isOptionActive,
            shouldShowDropdown
        ]
    )
}

export default useAutocomplete
